import React, { useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { XMarkIcon, PaperClipIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import axios from "axios";
import API_ROUTES from "../../config/apiRoutes";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../redux/slices/notificationSlice";
import Avatar from "../Common/Avatar";
import { useTranslation } from "../../contexts/LanguageContext";

export default function TicketDetailModal({ isOpen, onClose, ticket, onReplyAdded }) {
  const { t, tStatus } = useTranslation();
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.auth);

  useEffect(() => {
    if (ticket && ticket.replies) {
      setReplies(ticket.replies);
      setCurrentStatus(ticket.ticketStatus);
    }
  }, [ticket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [replies]);

  // Helper function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64String = await convertToBase64(file);
        setAttachment(base64String);
      } catch (error) {
        dispatch(showNotification({ message: t("ticketDetail.errorAttachment"), type: "error" }));
      }
    }
  };

  const handleSendReply = async () => {
    if (!message.trim() && !attachment) {
      dispatch(showNotification({ message: t("ticketDetail.errorEmptyReply"), type: "error" }));
      return;
    }

    setSending(true);
    try {
      // Send data as JSON with base64 attachment
      const replyData = {
        message: message.trim(),
        attachment: attachment || null
      };

      const response = await axios.patch(API_ROUTES.TICKETS.REPLY(ticket._id), replyData);

      if (response.data) {
        dispatch(showNotification({ message: t("ticketDetail.replySentSuccess"), type: "success" }));
        setMessage("");
        setAttachment(null);
        
        // Add the new reply to the list
        const newReply = {
          _id: new Date().getTime().toString(),
          senderId: admin?._id,
          message: message.trim(),
          attachment: attachment, // Already in base64 format
          createdAt: new Date().toISOString(),
        };
        setReplies([...replies, newReply]);
        
        if (onReplyAdded) {
          onReplyAdded();
        }
      }
    } catch (err) {
      console.error("Failed to send reply:", err);
      dispatch(
        showNotification({
          message: err.response?.data?.message || t("ticketDetail.replyFailed"),
          type: "error",
        })
      );
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;

    setUpdatingStatus(true);
    try {
      await axios.patch(API_ROUTES.TICKETS.UPDATE_STATUS(ticket._id), {
        ticketStatus: newStatus,
      });

      dispatch(showNotification({ message: t("ticketDetail.statusUpdatedSuccess"), type: "success" }));
      setCurrentStatus(newStatus);
      
      if (onReplyAdded) {
        onReplyAdded();
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      dispatch(
        showNotification({
          message: err.response?.data?.message || t("ticketDetail.statusUpdateFailed"),
          type: "error",
        })
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return "N/A";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-black text-white';
      case 'resolved':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!ticket) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="bg-dashboardDark px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-white">
                        {t("ticketDetail.titlePrefix")}{ticket.ticketNo} - {ticket.subject}
                      </Dialog.Title>
                      <p className="text-sm text-white/70 mt-1">
                        {t("ticketDetail.from")} {ticket.userId?.name} ({ticket.userId?.email})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Status Dropdown */}
                    <select
                      value={currentStatus}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      className={`px-3 py-1 rounded-full text-xs font-medium capitalize border-2 border-white bg-transparent text-white outline-none cursor-pointer hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <option value="open" className="text-dashboardDark">{t("ticketDetail.statusOpen")}</option>
                      <option value="resolved" className="text-dashboardDark">{t("ticketDetail.statusResolved")}</option>
                      <option value="closed" className="text-dashboardDark">{t("ticketDetail.statusClosed")}</option>
                    </select>

                    {/* Current Status Badge */}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(currentStatus)}`}>
                      {tStatus(currentStatus)}
                    </span>
                    
                    <button
                      onClick={onClose}
                      className="text-white hover:text-gray-300 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Messages Area */}
                <div className="h-[500px] overflow-y-auto bg-gray-50 p-6">
                  <div className="space-y-4">
                    {replies.map((reply, index) => {
                      const isAdmin = reply.senderId !== ticket.userId?._id;
                      
                      return (
                        <div
                          key={reply._id || index}
                          className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`flex gap-3 max-w-[70%] ${isAdmin ? "flex-row-reverse" : ""}`}>
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              {isAdmin ? (
                                admin?.profilePicture ? (
                                  <img
                                    src={admin.profilePicture}
                                    alt={admin.name}
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <Avatar name={admin?.name || "Admin"} size="sm" />
                                )
                              ) : ticket.userId?.profilePicture ? (
                                <img
                                  src={ticket.userId.profilePicture}
                                  alt={ticket.userId.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <Avatar name={ticket.userId?.name || "User"} size="sm" />
                              )}
                            </div>

                            {/* Message Content */}
                            <div className={`flex flex-col ${isAdmin ? "items-end" : "items-start"}`}>
                              <div
                                className={`rounded-lg px-4 py-3 ${
                                  isAdmin
                                    ? "bg-dashboardCoral text-white"
                                    : "bg-white text-gray-800 border border-gray-200"
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                                {reply.attachment && (
                                  <div className="mt-2">
                                    <a
                                      href={reply.attachment}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`text-xs underline flex items-center gap-1 ${
                                        isAdmin ? "text-white" : "text-dashboardCoral"
                                      }`}
                                    >
                                      <PaperClipIcon className="w-4 h-4" />
                                      {t("ticketDetail.viewAttachment")}
                                    </a>
                                  </div>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 mt-1">
                                {formatDate(reply.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Reply Input */}
                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("ticketDetail.typeReply")}
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-dashboardCoral resize-none"
                      />
                      {attachment && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                          <PaperClipIcon className="w-4 h-4" />
                          <span>{attachment.name}</span>
                          <button
                            onClick={() => setAttachment(null)}
                            className="text-red-500 hover:text-red-700"
                          >
                            {t("ticketDetail.remove")}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        title={t("ticketDetail.attachFile")}
                      >
                        <PaperClipIcon className="w-5 h-5 text-gray-600" />
                      </button>
                      <button
                        onClick={handleSendReply}
                        disabled={sending || (!message.trim() && !attachment)}
                        className="p-3 bg-dashboardCoral text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t("ticketDetail.sendReply")}
                      >
                        {sending ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <PaperAirplaneIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
